import styled from 'styled-components';

import { Theme } from 'utils/color/horizonTheme';

/**
 * `$isScrolledTo{X} props are used to add box-shadow to make it clear to the user
 * that there is more to see.
 */
export const PlotContainer = styled.div<{
  $isScrolledToBottom: boolean;
  $isScrolledToLeft: boolean;
  $isScrolledToRight: boolean;
  $isBladeInternal?: boolean;
}>`
  background: ${({ theme }) =>
    theme.themeName === Theme.Light ? theme.secondaryBackgroundColor : 'unset'};
  color: ${({ theme }) => theme.headerColor};
  max-height: 0;
  opacity: 0;
  position: relative;
  transition: all 0.5s ease-in-out;
  width: 100%;

  &.open {
    display: flex;
    flex-direction: column;
    max-height: 70vh;
    opacity: 0.9999;
    overflow: hidden;
  }

  ${({ $isScrolledToBottom, $isScrolledToLeft, $isScrolledToRight, $isBladeInternal, theme }) => {
    if ($isBladeInternal) {
      return `
        -webkit-box-shadow: none;
        -moz-box-shadow: none;
        box-shadow: none;
        `;
    }
    const spread: number = !$isScrolledToLeft && !$isScrolledToRight ? 5 : 0;
    const xOffset: number =
      !$isScrolledToLeft && !$isScrolledToRight
        ? 0
        : !$isScrolledToLeft
          ? 5
          : !$isScrolledToRight
            ? -5
            : 0;

    const yOffset: number = !$isScrolledToBottom ? (spread === 0 ? -5 : 0) : 10;

    const boxShadow = `inset ${xOffset}px ${yOffset}px 5px ${spread}px ${theme.lightShadow} !important`;

    return `
        -webkit-box-shadow: ${boxShadow};
        -moz-box-shadow: ${boxShadow};
        box-shadow: ${boxShadow};
        `;
  }}
`;

/**
 * `$isScrolledTo{X} props are used to add box-shadow to make it clear to the user
 * that there is more to see.
 */
export const PlotHeader = styled.div<{ $isScrolledToTop: boolean }>`
  align-items: center;
  background: ${({ theme }) => theme.secondaryBackgroundColor};
  box-shadow: 0px 2px 1px 0px #00000033;
  display: flex;
  height: 64px;
  justify-content: space-between;
  left: 0;
  padding: 1rem 5px 1rem 1rem;
  position: sticky;
  top: 0;
  width: 100%;
  z-index: 1;

  > div:first-of-type {
    align-items: center;
    display: flex;
    flex-grow: 1;
    justify-content: flex-start;
  }

  .ant-select {
    margin-left: 0.5rem;
    width: 480px;
  }

  button {
    border: none;

    &.active {
      color: ${({ theme }) => theme.teal};
    }
  }

  ${({ $isScrolledToTop, theme }) =>
    !$isScrolledToTop &&
    `
    -webkit-box-shadow: 0px 5px 5px 0px ${theme.lightShadow};
    -moz-box-shadow: 0px 5px 5px 0px ${theme.lightShadow};
    box-shadow: 0px 5px 5px 0px ${theme.lightShadow};
  `}
`;

export const OpenButtonContainer = styled.div`
  display: flex;
  height: 24px;
  justify-content: flex-end;

  button {
    background-color: ${({ theme }) => theme.tertiaryBackgroundColor};
    border: none;
    height: 24px;
    width: 42px !important;

    .anticon {
      vertical-align: 0.1rem;
    }
  }
`;

export const PlotScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
`;

export const PlotChartsColumn = styled.div`
  flex: 1;
  min-width: 0;
`;

export const PlotGrid = styled.div<{
  $isScrolledToLeft: boolean;
  $isScrolledToRight: boolean;
  $isBladeInternal?: boolean;
}>`
  display: flex;
  flex-direction: row;
  ${({ theme, $isBladeInternal }) =>
    `
      background: ${$isBladeInternal ? theme.tealLightOpacity : 'transparent'};
      ${!$isBladeInternal ? 'min-width: 1200px;' : ''}
    `}
  position: relative;
  padding-top: 1rem;

  ${PlotChartsColumn} svg {
    ${({ $isBladeInternal }) =>
      !$isBladeInternal &&
      `
          mix-blend-mode: multiply;
          padding-left: 0.5rem;
          padding-right: 1rem;
        `}

    max-height: 200px;
    transition: all 0.5s ease-in-out;
    overflow: visible;

    &.hidden {
      max-height: 0;
      opacity: 0;
    }

    .brush {
      rect.selection {
        fill: none;
        stroke: none;
      }
    }

    .selection-range {
      fill: none;
      stroke: ${({ theme }) => theme.teal};
      stroke-width: 2;
      stroke-linejoin: bevel;
    }

    .selection-range-label {
      font-size: 10px;
    }
  }
`;
